import { LogicalOperators } from "@validereinc/utilities";
import { format } from "date-fns";
import {
  FilterBuilderAllowedOperatorsMapping,
  FilterBuilderInputType,
  FilterBuilderOperators,
  FilterBuilderSupportedDataTypesType,
} from "./types";

export const equalityOperatorValue = "=";
export const emptyFieldKey = "empty_field";

/**
 * Configuration of which data type can have which operators.
 * Based on a combination of (data type + operator), input type can be different.
 */
export const dataTypeOperatorInputTypeMapping: FilterBuilderAllowedOperatorsMapping =
  {
    number: {
      [equalityOperatorValue]: "number-input",
      $eq: "number-input",
      $lt: "number-input",
      $lte: "number-input",
      $gt: "number-input",
      $gte: "number-input",
      $ne: "number-input",
    },
    integer: {
      [equalityOperatorValue]: "number-input",
      $eq: "number-input",
      $lt: "number-input",
      $lte: "number-input",
      $gt: "number-input",
      $gte: "number-input",
      $ne: "number-input",
    },
    string: {
      [equalityOperatorValue]: "string-input",
      $eq: "string-input",
      $exact: "string-input",
      $like: "string-input",
      $ne: "string-input",
      $exists: "boolean-input",
    },
    "pick-list": {
      [equalityOperatorValue]: "picklist-single-input",
      $in: "picklist-multi-input",
      $all: "picklist-multi-input",
      $eq: "picklist-single-input",
      $ne: "picklist-single-input",
    },
    "custom-pick-list": {
      [equalityOperatorValue]: "custom-pick-list-input",
      $in: "custom-pick-list-input",
      $all: "custom-pick-list-input",
      $eq: "custom-pick-list-input",
      $ne: "custom-pick-list-input",
    },
    date: {
      [equalityOperatorValue]: "date-input",
      $eq: "date-input",
      $lt: "date-input",
      $lte: "date-input",
      $gt: "date-input",
      $gte: "date-input",
    },
    boolean: {
      [equalityOperatorValue]: "boolean-input",
      $eq: "boolean-input",
      $ne: "boolean-input",
    },
    mixed: {
      [equalityOperatorValue]: "string-input",
      $eq: "string-input",
      $gt: "number-input",
      $gte: "number-input",
      $lt: "number-input",
      $lte: "number-input",
      $in: "list-input",
    },
  };

/**
 * Maps $and / $or to a user friendly value
 */
export const translateAndOr = (input: string) => {
  if (input === LogicalOperators.and) return "And";
  if (input === LogicalOperators.or) return "Or";
  return input;
};

/**
 * Maps operators ($<operator>) to a user friendly value
 */
export const translateOperator = (operator: FilterBuilderOperators) => {
  const operatorToTranslation: Record<FilterBuilderOperators, string> = {
    [equalityOperatorValue]: "Is",
    $eq: "Is equal to",
    $exact: "Is exactly equal to",
    $lt: "Less than",
    $lte: "Less than equal to",
    $gt: "Greater than",
    $gte: "Greater than equal to",
    $like: "Partially matches",
    $exists: "Does exist",
    $in: "Is one of",
    $all: "Has all of",
    $ne: "Is Not",
  };
  return operatorToTranslation[operator] ?? operator;
};

/**
 * Returns the deep reference within an object based on an array of keys.
 * It does NOT return a copied value.
 *
 * This function can be used for mutating an object at a certain key
 * @param object Input object
 * @param keys Array keys, a mix of numbers and string
 *
 * Example:
 * if `obj =  {"a": [{ "d": 12, "f": [{ "key": { "key2": 12 } }] }]}`
 *
 * then `getDeepReference(obj, ["a", 0, "f", 0, "key"]) === obj.a[0].f[0].key` is `true`
 *
 * but `getDeepReference(obj, ["a", 0, "f", 0, "key"]) === obj.a[0].f[0].key === { "key2": 12 }` is `false`
 */
export const getDeepReference = (obj: any, keys: Array<string | number>) =>
  keys.reduce(
    (acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
    obj
  );

/**
 * This function helps esToolkit.set() to work with keys that have "." in them.
 *
 * Example Input: `["key1", "key2", "custom_attributes.country"]`
 *
 * gets converted to: `key1.key2.["custom_attributes.country"]`
 *
 * When passed to esToolkit,
 *
 * it will be `{key1: {key2: {"custom_attributes.country": {...}}}}`
 *
 * Instead of `{key1: {key2: {custom_attributes: {country: {...}}}}}`
 *
 * @param arrayKey Array of keys (Mix of strings and numbers)
 */
export const getStringKeyFromArrayKey = (arrayKey: Array<string | number>) => {
  return arrayKey
    .map((singleKey) => {
      if (typeof singleKey === "string" && singleKey.includes(".")) {
        return `["${singleKey}"]`;
      }
      return singleKey;
    })
    .join(".");
};

/**
 * For a given data_type (number, string, etc), an optional operator ($in, $like, etc),
 * and an optional format (which currently only works for date values),
 * a sample value is returned which prepopulates each filter entry.
 */
export const generateSampleValueForFilterBuilder = ({
  data_type,
  operator,
  formatTo,
  operatorMappingOverride,
}: {
  data_type: FilterBuilderSupportedDataTypesType;
  operator?: FilterBuilderOperators;
  formatTo?: string;
  operatorMappingOverride?: FilterBuilderAllowedOperatorsMapping;
}) => {
  const operatorMapping =
    operatorMappingOverride ?? dataTypeOperatorInputTypeMapping;
  let finalOperator = operator;

  if (!finalOperator) {
    finalOperator = Object.keys(operatorMapping[data_type])[0] as
      | FilterBuilderOperators
      | undefined;
  }

  if (!finalOperator)
    throw new Error("inputType doesn't have any operators defined for it");

  const inputType = operatorMapping[data_type][finalOperator];

  if (!inputType) throw new Error("Operator doesn't exist for data_type");

  if (inputType === "custom-pick-list-input") {
    return finalOperator === "$in" || finalOperator === "$all" ? [] : "";
  }

  const inputTypeToSampleValue: Record<FilterBuilderInputType, any> = {
    "string-input": "Sample Value",
    "number-input": 0,
    "boolean-input": true,
    "picklist-single-input": "",
    "picklist-multi-input": [],
    "date-input": new Date(),
    "list-input": [],
    "custom-pick-list-input": "",
  };

  const unformattedValue = inputTypeToSampleValue[inputType];

  if (formatTo) {
    if (inputType === "date-input") return format(unformattedValue, formatTo);
  }

  return unformattedValue;
};

/**
 * Safe wrapper for stringifying an unknown object with a fallback for when there are errors.
 *
 * @param stringifyArguments Arguments that get passed directly to JSON.stringify
 * @param fallback a fallback string for when JSON.stringify throws an error
 * @returns stringified value
 */
export const safeJSONStringify = (
  stringifyArguments: Parameters<typeof JSON.stringify>,
  fallback = ""
) => {
  try {
    return JSON.stringify(...stringifyArguments);
  } catch (_) {
    return fallback;
  }
};
